import { IFpayParams } from "@/api/apiRsp/IFWalletRsp";
import {
  AmountDivisorConversion,
  AmountMultiplierConversion,
  exchangeRateConversion,
} from "@/Core/utils/currencyDealCenter";

export interface DepositBonus {
  amount: number;
  currencyType: string;
}

export const isFunBonus = (bonus?: DepositBonus): boolean =>
  bonus?.currencyType === "FUN" || bonus?.currencyType === "LOCK_FUN";

export function getAccountDepositBonus(
  data: IFpayParams,
  value: number,
  rechargeCurrencyType: string
): DepositBonus | undefined {
  const rule = data?.accountRebateRule?.reduce((matched, item) => {
    if (
      item.rechargeCurrencyType === rechargeCurrencyType &&
      value >= AmountDivisorConversion(item.minRechargeAmount, {
        currencyType: item.rechargeCurrencyType,
      }) &&
      (!matched || item.minRechargeAmount >= matched.minRechargeAmount)
    ) {
      return item;
    }
    return matched;
  }, undefined);
  const { baseRate, rates } = data?.currencyRateInfo || {};
  if (!rule || !baseRate || !rates?.length) return undefined;

  const sourceRate = rates.find((item) => item.currencyType === rechargeCurrencyType);
  const targetRate = rates.find((item) => item.currencyType === rule.rebateCurrencyType);
  if (!sourceRate || !targetRate) return undefined;

  const baseAmount = exchangeRateConversion(
    {
      amount: AmountMultiplierConversion(value, { currencyType: rechargeCurrencyType }),
      currency: rechargeCurrencyType,
    },
    baseRate.currencyType,
    sourceRate.rate
  );
  const rewardAmount = exchangeRateConversion(
    { amount: baseAmount, currency: baseRate.currencyType },
    rule.rebateCurrencyType,
    1 / targetRate.rate
  );

  return {
    amount: rewardAmount * rule.rebateRate / 100,
    currencyType: rule.rebateCurrencyType,
  };
}

export function getLegacyDepositBonus(
  data: Pick<IFpayParams, "rebateRuleItems" | "limitRebateAmount" | "limitRebateAmountCurrencyType">,
  value: number,
  rechargeCurrencyType: string
): { bonus?: DepositBonus; minimum?: DepositBonus } {
  const items = data?.rebateRuleItems;
  if (!items?.length) return {};

  const rule = items.reduce((matched, item) => {
    const minAmount = AmountDivisorConversion(item.rechargeAmount, {
      currencyType: item.rechargeAmountCurrencyType || rechargeCurrencyType,
    });
    if (value > 0 && value >= minAmount) {
      if (!matched) return item;
      const matchedMin = AmountDivisorConversion(matched.rechargeAmount, {
        currencyType: matched.rechargeAmountCurrencyType || rechargeCurrencyType,
      });
      if (minAmount > matchedMin ||
        (minAmount === matchedMin && item.rebateRate > matched.rebateRate)) {
        return item;
      }
    }
    return matched;
  }, undefined);

  if (!rule) {
    return {
      minimum: {
        amount: Number(items[0].rechargeAmount),
        currencyType: items[0].rechargeAmountCurrencyType || rechargeCurrencyType,
      },
    };
  }

  const currencyType = rule.rebateCurrencyType || rechargeCurrencyType;
  let amount = value * rule.rebateRate / 100;
  if (data.limitRebateAmount !== null && data.limitRebateAmount !== undefined) {
    amount = Math.min(amount, AmountDivisorConversion(data.limitRebateAmount, {
      currencyType: data.limitRebateAmountCurrencyType || currencyType,
    }));
  }
  if (!(amount > 0)) return {};

  return {
    bonus: {
      amount: AmountMultiplierConversion(amount, { currencyType }),
      currencyType,
    },
  };
}
